<template>
  <div class="container-fluid py-4">
    <div class="card shadow-sm p-4">
      <div class="row align-items-center mb-3">
        <div class="col-12 col-md-6">
          <h5 class="mb-0">
            <i class="fa fa-file-invoice me-2 text-danger"></i>
            Nueva Nota de Crédito
          </h5>
          <small class="text-muted">{{ factura.header.invoice_type }} #{{ facturaTempNumber }}</small>
        </div>
        <div class="col-12 col-md-2 text-md-end mt-3 mt-md-0">
          <argon-button class="btn btn-secondary w-100" @click="goBack">
            <i class="fa fa-arrow-left me-1"></i> Volver
          </argon-button>
        </div>
        <div class="col-12 col-md-2 text-md-end mt-3 mt-md-0">
          <argon-button class="btn btn-success w-100" @click="guardarFactura" :disabled="isSaved || factura.lines.length === 0">
            <i class="fa fa-save me-1"></i> {{ isSaving ? 'Guardando...' : 'Guardar Nota' }}
          </argon-button>
        </div>
        <div class="col-12 col-md-2 text-md-end mt-3 mt-md-0">
          <argon-button class="btn btn-success w-100" @click="printPDF" :disabled="factura.lines.length === 0">
            <i class="fa fa-print me-1"></i> Imprimir
          </argon-button>
        </div>
      </div>

      <div class="card bg-light border-0 mb-4 p-3">
        <div class="row g-3 align-items-end">
          <div class="col-md-6">
            <label class="form-label mb-1">Cliente</label>
            <ArgonLookup
                :options="clientes"
                v-model="clienteSeleccionado"
                display-key="display_name"
                track-by="id"
                placeholder="Escriba nombre o DUI del cliente..."
            >
              <template #option="{ option }">
                <div class="d-flex justify-content-between align-items-center w-100">
                  <span>
                    <span class="font-weight-bold">{{ option.name }}</span>
                  </span>
                  <span class="text-sm text-muted">
                    DUI: {{ option.nit }}
                  </span>
                </div>
              </template>
            </ArgonLookup>
          </div>
          <div class="col-md-3">
            <label class="form-label mb-1">Fecha de Emisión</label>
            <input type="date" class="form-control" v-model="factura.fecha" />
          </div>
          <div class="col-md-3">
            <label class="form-label text-sm">Tipo de documento</label>
            <select v-model="factura.header.invoice_type" class="form-select" disabled>
              <option value="NOTA_CREDITO">Nota de Crédito</option>
            </select>

          </div>
        </div>
      </div>

      <div class="card border mb-4 p-3">
        <div class="row g-3 align-items-end">
          <div class="col-md-6">
            <label class="form-label mb-1">Factura Original a Afectar</label>
            <ArgonLookup
                :options="facturasOriginales"
                v-model="facturaOriginalSeleccionada"
                display-key="display_name"
                track-by="id"
                placeholder="Seleccione la Factura (No cancelada/anulada)..."
                :disabled="!clienteSeleccionado || isSaved"
            >
              <template #option="{ option }">
                <div class="d-flex justify-content-between align-items-center w-100">
                  <span>
                    <span class="font-weight-bold">{{ option.full_number }}</span> -
                    {{ formatDate(option.date_issued) }}
                  </span>
                  <span :class="['text-sm font-weight-bold', getStateClass(option.state)]">
                    Total: ${{ formatCurrency(option.total) }}
                  </span>
                </div>
              </template>
            </ArgonLookup>
            <small v-if="factura.header.related_document" class="text-success mt-1 d-block">
                Documento Relacionado: {{ factura.header.related_doc }}
            </small>
          </div>
          <div class="col-md-6 text-md-end mt-3 mt-md-0">
            <argon-button class="btn btn-success w-100" @click="fetchFacturasOriginales(true)" :disabled="!clienteSeleccionado">
              <i class="fa fa-sync me-1"></i> Buscar Facturas Disponibles
            </argon-button>
          </div>
        </div>
      </div>

      <div class="table-responsive">
        <table class="table table-hover align-middle">
        <thead class="table-light">
            <tr>
                <th class="text-center">Incluir</th>
                <th>Producto</th>
                <th class="text-center">Cant. Original</th>
                <th class="text-center">Modo NC</th>
                <th class="text-center">Valor a Afectar</th>
                <th class="text-end">Precio Unit.</th>
                <th class="text-end">Total Original</th>
                <th class="text-end">Total Nota</th>
            </tr>
        </thead>
        <tbody>
            <tr v-for="(item, index) in facturaOriginal.lines" :key="index">
                <td class="text-center">
                    <input type="checkbox" v-model="item.selected" @change="updateNotaCreditLines(item)" :disabled="isSaved">
                </td>
                <td>{{ item.description || item.nombre }}</td>
                <td class="text-center">{{ item.quantity }}</td>

                <td class="text-center">
                    <select v-model="item.credit_mode"
                      @change="handleCreditModeChange(item)"
                      :disabled="!item.selected || isSaved"
                      class="form-select form-select-sm">
                        <option value="QUANTITY">Cantidad</option>
                        <option value="AMOUNT">Monto</option>
                    </select>
                </td>

                <td class="text-center">
                    <input v-if="item.credit_mode === 'QUANTITY'"
                            type="number"
                            v-model.number="item.quantity_note"
                            @change="updateNotaCreditLines(item)"
                            :max="item.quantity"
                            :disabled="!item.selected || isSaved"
                            min="0"
                            class="form-control form-control-sm text-center">

                    <input v-else
                            type="number"
                            v-model.number="item.amount_note"
                            @change="updateNotaCreditLines(item)"
                            :max="calculateLineTotalOriginal(item)"
                            :disabled="!item.selected || isSaved"
                            min="0"
                            step="0.01"
                            class="form-control form-control-sm text-end">
                </td>

                <td class="text-end">${{ formatCurrency(Number(item.unit_price).toFixed(2)) }}</td>
                <td class="text-end">${{ calculateLineTotalOriginal(item).toFixed(2) }}</td>
                <td class="text-end">${{ calculateLineTotalNote(item).toFixed(2) }}</td>
            </tr>
            <tr v-if="facturaOriginal.lines.length === 0">
                <td colspan="8" class="text-center text-muted">Seleccione una factura para ver sus líneas.</td>
            </tr>
        </tbody>
    </table>
      </div>

      <div class="d-flex justify-content-end mt-3">
        <div class="text-end">
            <p class="mb-1"><strong>Subtotal Bruto:</strong> ${{ formatCurrency(subtotalBruto.toFixed(2)) }}</p>
            <p class="mb-1 text-danger"><strong>Descuento Total:</strong> ${{ formatCurrency(totalDescuento.toFixed(2)) }}</p>
            <p class="mb-1"><strong>Subtotal Neto (Base Imp.):</strong> ${{ formatCurrency(subtotalNeto.toFixed(2)) }}</p>
            <p class="mb-1"><strong>IVA ({{tax_symbol}}%):</strong> ${{ formatCurrency(iva.toFixed(2)) }}</p>
            <h5 class="mb-0 text-danger"><strong>Total de la NOTA:</strong> ${{ formatCurrency(total.toFixed(2)) }}</h5>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { useInvoicePrinter } from "@/composables/useInvoicePrinterPOS";
import { useDTEFiscal } from "@/composables/useDTEFiscal";
import { useCompanyConfig } from '@/composables/useCompanyConfig';
import ArgonButton from "@/components/ArgonButton.vue";
import ArgonLookup from "@/components/ArgonLookup.vue";
import axios from 'axios';
const companyId = localStorage.getItem('companyId');
export default {
  name: "CreateCreditNote", // Renombrado
  components: { ArgonButton, ArgonLookup },
  setup() {
    const { companyConfig, isLoadingCiaConfig, fetchCompanyConfig } = useCompanyConfig();
    fetchCompanyConfig();
    return { companyConfig, isLoadingCiaConfig };
  },
  data() {
    return {
      isSaving: false,
      isSaved: false,
      facturaTempNumber: "0000",
      clientes: [],
      clientsBulk: [],
      // 🛑 NOTA DE CRÉDITO VARIABLES 🛑
      facturasOriginales: [],
      facturaOriginalSeleccionada: null,
      facturaOriginal: { lines: [] }, // Factura completa (para mostrar líneas)
      // ---------------------------------
      factura: {
        client_id: "",
        fecha: new Date().toISOString().split("T")[0],
        lines: [], // Líneas seleccionadas para la NOTA DE CRÉDITO
        full_number: "",
        tax_rate: 0,
        mapClientToHeader: null,
        header: {
          emitter_nit: "12345678-9",
          emitter_name: "Mi Empresa POS",
          invoice_type: "NOTA_CREDITO", // DTE 05
          dte_type: '05', // DTE 05
          currency: "USD",
          state: "EMITTED",
          full_number: null,
          date_issued: null,
          subtotal: 0,
          total_taxes: 0,
          total: 0,
          dte_trace_code: '',
          dte_stamp_code: '',
          dte_url_code: '',
          client_address: null,
          client_nrc: null,
          client_document_type: '36',
          client_department_code: null,
          client_municipality_code: null,
          client_activity_code: '99999',
          client_activity_desc: 'Venta al consumidor',
          client_email: null,
          client_phone: null,
          excent: null,
          client_type: null,
          // 🛑 Nuevo campo para el documento original (se llena en watch/mapOriginalFacturaToNoteHeader)
          related_document: null,
        },
      },
      clienteSeleccionado: null,
      tax_rate: 0,
    };
  },
  watch: {
    companyConfig(newVal) {
      this.tax_rate = newVal.default_tax_rate;
      this.factura.header.emitter_nit = newVal.dte_client_id;
      this.tax_symbol = (newVal.default_tax_rate * 100).toFixed(0);
    },
    clienteSeleccionado(newClient) {
      // 1. Resetear facturas originales y líneas
      this.facturaOriginalSeleccionada = null;
      this.facturaOriginal.lines = [];
      this.facturasOriginales = [];
      this.factura.lines = [];

      if (!newClient) {
        this.factura.client_id = "";
        this.factura.header.client_name = 'Consumidor Final';
        this.factura.header.client_nit = '0000000000';
        this.mapClientToHeader({ type: 'NATURAL' });
        return;
      }

      // 2. Mapear cliente y dte_type (será 05)
      const fullClientData = this.clientsBulk.find(c => c.id === newClient.id);
      this.factura.client_id = newClient.id; // Asignar client_id
      this.factura.header.client_type = newClient.type || 'NATURAL';
      this.mapClientToHeader(newClient.type ? newClient : fullClientData);

      // 3. Obtener facturas disponibles para este cliente
      this.fetchFacturasOriginales();
    },
    facturaOriginalSeleccionada(newFacturaHeader) {
      // 1. Resetear líneas de la nota
      this.factura.lines = [];

      if (newFacturaHeader) {
        // 2. Mapear info de la factura original al header de la nota (campo related_document)
        this.mapOriginalFacturaToNoteHeader(newFacturaHeader);
        // 3. Cargar líneas de la factura original
        console.log(newFacturaHeader.id, newFacturaHeader.full_number);
        this.fetchInvoiceLines(newFacturaHeader.id);
      } else {
        this.facturaOriginal.lines = [];
        this.factura.header.related_doc= null;
      }
    }
  },
  computed: {
    // Los cómputos usan this.factura.lines (las líneas de la NOTA DE CRÉDITO)
    totalDescuento() {
      return this.factura.lines.reduce((sum, i) => sum + Number(i.discount), 0);
    },
    subtotalBruto() {
      // (Precio * Cantidad NOTA)
      return this.factura.lines.reduce((sum, i) => sum + (Number(i.quantity) * Number(i.precio)), 0);
    },
    subtotalNeto() {
      // Base Imponible de la NOTA
      return this.subtotalBruto - this.totalDescuento;
    },
    iva() {
      // IVA de la NOTA (debe recalcularse en updateNotaCreditLines)
      return this.factura.lines.reduce((sum, i) => sum + Number(i.tax_amount), 0);
    },
    total() {
      // Total Final de la NOTA
      return Number(this.subtotalNeto) + Number(this.iva);
    },
  },
  methods: {
      handleCreditModeChange(item) {
        // Si el usuario selecciona MONTO, aseguramos que la línea esté seleccionada
        // para habilitar el input y el procesamiento.
        if (item.credit_mode === 'AMOUNT') {
            item.selected = true;
            // Opcional: Si cambiamos a MONTO, limpiamos el valor de la cantidad
            item.quantity_note = 0;
            item.amount_note = item.line_total_note || 0; // Inicializar con el total actual
        } else {
            // Si el usuario selecciona CANTIDAD, limpiamos el monto
            item.amount_note = 0;

            // Si item.selected es verdadero, forzamos el recálculo
            if (item.selected) {
                item.quantity_note = item.quantity;
            }
        }

        // Llamar a la función principal para que recalcule y actualice el array final
        this.updateNotaCreditLines(item);
    },
    calculateLineTotalOriginal(item) {
      // Total bruto de la línea original (antes de descuentos e IVA, solo para referencia visual)
      return Number(item.quantity) * Number(item.unit_price);
    },

    // 🛑 MODIFICADO: calculateLineTotalNote (Solo ajusta el cálculo)
    calculateLineTotalNote(item) {
      // Usa el total calculado y guardado en la propiedad 'line_total_note'
      return item.line_total_note || 0;
    },
    formatCurrency(value) {
        if (value === null || isNaN(value)) {
            return '0.00';
        }
        // Usamos 'es-ES' o 'en-US' para coma de miles y punto decimal
        return new Intl.NumberFormat('en-US', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
            useGrouping: true // Asegura el separador de miles
        }).format(value);
    },
    formatDate(dateString) {
        const date = new Date(dateString);
        return date.toLocaleString('es-ES', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    },
    // 🛑 LÓGICA DE CÁLCULO DE LÍNEA PARA LA NOTA DE CRÉDITO 🛑
    /*calculateLineTotalNote(item) {
        // Usa la tasa de la compañía, ya que la línea original solo tiene el monto
        const taxRate = this.tax_rate || 0.13;
        const quantity = item.quantity_note || 0;
        const price = item.unit_price || 0;
        const baseBruta = price * quantity;
        const baseNeta = baseBruta;
        // Asumiendo que las Notas de Crédito deben seguir la lógica DTE 03 (Base + IVA)
        const total = baseNeta * (1 + parseFloat(taxRate));
        return parseFloat(total.toFixed(4)); // Usamos 4 decimales para precisión antes del 2 final
    },*/

    updateNotaCreditLines(lineItem) {
      const taxRate = this.tax_rate;
      let quantityToCredit = 1;
      let baseMonetariaBruta = 0; // Representa el valor bruto total a acreditar

      // 1. Lógica del Checkbox / Reset (Mantenida)
      if (!lineItem.selected) {
          lineItem.quantity_note = 0;
          lineItem.amount_note = 0;
          lineItem.line_total_note = 0;
      } else if (lineItem.credit_mode === 'QUANTITY' && lineItem.quantity_note === 0) {
          lineItem.quantity_note = lineItem.quantity;
      }

      if (!lineItem.selected || (lineItem.credit_mode === 'QUANTITY' && lineItem.quantity_note === 0) || (lineItem.credit_mode === 'AMOUNT' && lineItem.amount_note === 0)) {
          this.factura.lines = this.factura.lines.filter(l => l.id !== lineItem.id);
          lineItem.selected = false;
          lineItem.line_total_note = 0;
          return;
      }

      //  2. Cálculo Base según el MODO (Determinamos baseMonetariaBruta)
      if (lineItem.credit_mode === 'QUANTITY') {
          quantityToCredit = lineItem.quantity_note;

          if (quantityToCredit > lineItem.quantity) {
              quantityToCredit = lineItem.quantity;
              lineItem.quantity_note = quantityToCredit;
              this.$swal('Atención', 'La cantidad de la nota no puede exceder la cantidad original.', 'warning');
          }

          // Base Bruta = Cantidad * Precio Unitario (sin descontar IVA ni descuentos)
          baseMonetariaBruta = quantityToCredit * lineItem.unit_price;

      } else { // MODO MONTO
          baseMonetariaBruta = parseFloat(lineItem.amount_note) || 0;
          quantityToCredit = 1; // Fijo en 1 para el inventario

          const maxTotalOriginal = this.calculateLineTotalOriginal(lineItem); // Total de la línea (Base + IVA)

          if (baseMonetariaBruta > maxTotalOriginal) {
              baseMonetariaBruta = maxTotalOriginal;
              lineItem.amount_note = baseMonetariaBruta;
              this.$swal('Atención', 'El monto a acreditar no puede exceder el total de la línea original.', 'warning');
          }
      }

      // 3. CÁLCULO FINAL DE IMPUESTOS Y TOTALES

      // Si la nota es por cantidad, aplicamos descuento por unidad. Si es por monto, no aplicamos descuento unitario.
      const descuentoTotal = lineItem.credit_mode === 'QUANTITY'
          ? lineItem.discount_per_unit * quantityToCredit
          : 0; // Asumimos que el monto ya es la base neta.

      const subtotalNetoLinea = baseMonetariaBruta - descuentoTotal; // Base Imponible

      let ivaMonto = subtotalNetoLinea * taxRate;
      const totalFinalNota = subtotalNetoLinea + ivaMonto;

      // Actualizar la propiedad interna para el display de la tabla
      lineItem.line_total_note = totalFinalNota;

      // 4. Construir/Reemplazar el objeto de la NOTA FINAL (factura.lines)
      const lineToCredit = {
          id: lineItem.id,
          nombre: lineItem.description,
          cantidad: quantityToCredit,
          quantity: quantityToCredit||1,
          unit_price: subtotalNetoLinea,
          precio: subtotalNetoLinea,
          product_id: lineItem.product_id,
          discount: Number(descuentoTotal),
          tax_rate: taxRate,
          tax_amount: Number(ivaMonto),
          sub_total_brut: Number(baseMonetariaBruta),
          sub_total: Number(subtotalNetoLinea), // Base Imponible
          line_total: Number(totalFinalNota) // Total final de la línea
      };

      // 5. Inserción en la factura.lines (se mantiene)
      const existingIndex = this.factura.lines.findIndex(l => l.id === lineItem.id);
      if (existingIndex > -1) {
          this.factura.lines.splice(existingIndex, 1, lineToCredit);
      } else {
          this.factura.lines.push(lineToCredit);
      }
    },

    // 🛑 Mapeo de la Factura Original al Header de la Nota
    mapOriginalFacturaToNoteHeader(originalFactura) {
        this.factura.header.related_doc =originalFactura.full_number;
        // Para el receptor de la nota, usamos los datos del cliente que se está mapeando
        this.mapClientToHeader(this.clienteSeleccionado);
    },

    // 🛑 FETCH FACTURAS ORIGINALES 🛑
    async fetchFacturasOriginales(manualFetch = false) {
        if (!this.clienteSeleccionado) return;
        const clientId = this.clienteSeleccionado.id;
        const token = localStorage.getItem("accessToken");
        const config = { headers: { 'Authorization': `Bearer ${token}` } };

        const url = `${this.$store.state.apiURL}invoice_headers?company=${companyId}&client_id=${clientId}&state=EMITTED&invoice_type=FACTURA`;

        try {
            const response = await axios.get(url, config);
            this.facturasOriginales = response.data.map(item => ({
                ...item,
                id: item.id,
                total: parseFloat(item.total),
                display_name: `${item.full_number} (${this.formatDate(item.date_issued)}) - ${this.formatCurrency(item.total)}`
            }));
            if(manualFetch) this.$swal('Búsqueda Completa', `Encontradas ${this.facturasOriginales.length} facturas no canceladas.`, 'info');
        } catch (error) {
            console.error("Error fetching original invoices:", error);
            this.$swal('Error', 'No se pudieron cargar las facturas del cliente.', 'error');
        }
    },

    // 🛑 FETCH LÍNEAS DE FACTURA ORIGINAL 🛑
    async fetchInvoiceLines(invoiceId) {
        const token = localStorage.getItem("accessToken");
        const config = { headers: { 'Authorization': `Bearer ${token}` } };

        const url = `${this.$store.state.apiURL}invoice_headers/${invoiceId}?include_lines=true`;

        try {
            const response = await axios.get(url, config);
            // Mapear líneas para añadir las propiedades de control
            this.facturaOriginal.lines = response.data.lines.map(line => ({
                ...line,
                selected: false,
                credit_mode: 'QUANTITY', // 🆕 Modo por defecto
                quantity_note: line.quantity, // Cantidad a devolver
                amount_note: 0, // 🆕 Monto a devolver (solo si mode=AMOUNT)
                line_total_note: 0, // 🆕 Total final de la línea de la nota (actualizado en la lógica)
                discount_per_unit: (parseFloat(line.discount) / parseFloat(line.quantity)) || 0,
            }));
        } catch (error) {
            console.error("Error fetching invoice lines:", error);
            this.$swal('Error', 'No se pudieron cargar las líneas de la factura.', 'error');
        }
    },

    // ... (El resto de métodos se mantiene, adaptado para la lógica de nota de crédito) ...

    async fetchCustomers(config) {
      axios.get(this.$store.state.apiURL+'clients?company='+companyId,config)
      .then((result)=>{
        this.clientes=result.data.map(item => ({
          id: item.id,
          name: item.full_name || item.name,
          nit: item.nit || 'CF',
          type: item.type || 'NATURAL',
          // Campo combinado para la búsqueda y el input
          display_name: `${item.full_name || item.name} - NIT: ${item.nit || 'CF'}`
        }));
        this.clientsBulk= result.data;
        //console.log(this.clientsBulk);
      })
      .catch(error => {
        console.log(error)
        self.$swal({
            title: 'Sesión',
            text: 'El tiempo de sesión a vencido, ingresa nuevamente.',
            type: 'warning',
            icon:'warning',
            showCancelButton: false,
            confirmButtonColor:'#bf4f51',
            showCloseButton: true,
            showLoaderOnConfirm: false
        }).then(function(){
            localStorage.removeItem('user');
            localStorage.removeItem('accessToken');
            self.$router.push('/signin');
        });
      });
    },
    // fetchProducts ELIMINADO

    // ... (restantes métodos utilitarios: goBack, formatCurrency, formatDate, etc.)

    // MÉTODOS DE MANEJO DE FACTURA (ACTUALIZADOS)
    mapClientToHeader(client) {
        // Utiliza el operador de fusión de nulos (??) para los valores por defecto
        this.factura.header.client_name = client?.name || 'Consumidor Final';
        this.factura.header.client_nit = client?.nit || '0000000000';
        this.factura.header.client_address = client?.fiscal_address || client?.commercial_address || 'Sin dirección';
        this.factura.header.client_nrc = client?.nrc || null;
        this.factura.header.client_document_type = client?.document_type || '36'; // 36=Consumidor Final
        this.factura.header.client_department_code = client?.department_code || null;
        this.factura.header.client_municipality_code = client?.municipality_code || null;
        this.factura.header.client_activity_code = client?.activity_code || '99999'; // 99999=Varios/CF
        this.factura.header.client_activity_desc = client?.activity_desc || 'Venta al consumidor';
        this.factura.header.client_email = client?.email || null;
        this.factura.header.client_phone=client?.phone || null;
        this.factura.header.excent=client?.excent || null;
    },

    async getNextSerial() {
        // Usamos DTE 05
        const serialType = 5;
        const isExempt = this.factura.header.excent === 1;

        const token = localStorage.getItem("accessToken");
        const apiURL = this.$store.state.apiURL;

        if (!token) {
            this.$swal('Error de Sesión', 'Acceso denegado. Reinicie sesión.', 'error');
            throw new Error("No token");
        }

        try {
            const payload = {
                type: serialType, // ⬅️ Enviamos el número entero 5
                company: this.companyConfig.id,
                excent: isExempt ? 1 : 0
            };

            const response = await axios.post(
                apiURL + 'serial/reserve_next',
                payload,
                { headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' } }
            );

            return response.data;
        } catch (error) {
            console.error("Error al obtener y reservar el serial:", error.response || error);
            const errorText = error.response?.data?.error || 'No se pudo reservar el número consecutivo. Intente de nuevo.';
            this.$swal('Error de Serial', errorText, 'error');
            throw new Error("Serial Reservation Failed");
        }
    },

    async guardarFactura() {
      if (this.isSaving) return;

      // 🛑 Validar que la factura original esté seleccionada y haya líneas para la nota
      if (!this.facturaOriginalSeleccionada || this.factura.lines.length === 0) {
          this.$swal('Error', 'Debe seleccionar una Factura Original y agregar al menos una línea a la Nota de Crédito.', 'error');
          return;
      }

      this.isSaving = true;
      const token = localStorage.getItem("accessToken");
      const apiURL = this.$store.state.apiURL;
      const user = JSON.parse(localStorage.getItem('userId'));
      const fullNumberCurrent = await this.getNextSerial();

      const sendDTE = (this.factura.header.invoice_type === 'TICKET') ? 0 : this.companyConfig.send_dte;
      this.facturaTempNumber = fullNumberCurrent.full_number;

      // --- A. Preparar Header para la BD ---
      const headerData = {
          ...this.factura.header,
          emitter_name: this.companyConfig.emitter_name,
          dte_env: this.companyConfig.dte_env,
          emitter_nit: this.factura.header.emitter_nit,
          client_id: this.factura.client_id,
          // client_nit y client_name se usan del header mapeado
          date_issued: this.factura.fecha + ' ' + new Date().toTimeString().split(' ')[0],

          sub_total_brut: this.subtotalBruto.toFixed(2),
          subtotal: this.subtotalNeto.toFixed(2),
          total_taxes: this.iva.toFixed(2),
          total_discount: this.totalDescuento.toFixed(2),
          total: this.total.toFixed(2), //

          company: this.companyConfig.id,
          full_number: fullNumberCurrent.full_number,
          series: fullNumberCurrent.series,
          number: fullNumberCurrent.number,
          created_by: user ? user : null,
          related_doc: this.facturaOriginalSeleccionada.full_number, // ID interno de la factura original
          //related_doc_full_number: this.facturaOriginalSeleccionada.full_number,
      };

      // --- B. Mapear Líneas para la BD ---
      const linesData = this.factura.lines.map(line => ({
          product_id: line.product_id,
          description: line.nombre,
          quantity: line.cantidad,
          unit_price: line.precio,
          discount: line.discount,
          tax_rate: line.tax_amount,
          sub_total: line.sub_total,
          sub_total_brut: line.sub_total_brut,
          line_total: line.line_total,
          warehouse_id: 1, // Asumo 1 por defecto
          allocated: 1
      }));

      // 🛑 C. PAYLOAD FINAL 🛑
      const payload = {
          header: headerData,
          lines: linesData,
          emit_dte: sendDTE,
          company: this.companyConfig.id,
          // Pasar el objeto related_document completo para el builder DTE

      };

      const config = { headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' } };
      console.log(payload)
      // ... (Lógica de envío al endpoint 'invoices/transaction') ...
      try {
          const response = await axios.post(apiURL + 'invoices/transaction', payload, config);

          this.$swal({
              title: '¡Éxito!',
              text: `Nota de Crédito ${response.data.full_number} procesada.`,
              icon: 'success',
              confirmButtonColor: '#0b9257'
          }).then(() => {
              this.factura.header.dte_trace_code= response.data.dte_result.traceability_code;
              this.factura.header.dte_stamp_code= response.data.dte_result.stamp_code;
              this.factura.header.dte_url_code= response.data.dte_result.url_code;

              this.isSaved=true;
              this.printPDF();
          });

      } catch (error) {
          console.error("Error procesar Nota de Crédito:", error.response || error);
          this.$swal({
              title: 'Error de Transacción',
              text: error.response?.data?.error || 'Ocurrió un error desconocido. La transacción fue revertida (Rollback).',
              icon: 'error',
              confirmButtonColor: '#0b9257'
          });
      } finally {
          this.isSaving = false;
      }
    },

    async printPDF() {
      if (this.factura.lines.length === 0) {
          alert('Debe agregar productos para imprimir.');
          return;
      }
      // Creamos un objeto que contenga todos los datos necesarios (factura, subtotalBruto)
      const facturaData = {
          ...this.factura,
          header: {
              ...this.factura.header,
              full_number:this.facturaTempNumber,
              subtotal:this.subtotalNeto,
              total_discount:this.totalDescuento,
              total_taxes:this.iva,
              total:this.total,
              sub_total_brut: this.subtotalBruto, // Agregamos el bruto para el composable
              dte_type:this.factura.header.dte_type
          }
      };
      // LLAMADA CENTRALIZADA: isReimpresion = false
      let formatToUse = this.companyConfig.invoice_default_format
      if(facturaData.header.invoice_type=='TICKET'){
          useInvoicePrinter(facturaData,this.companyConfig, true);
      }else{
        if (formatToUse == 3 || (formatToUse != 1 && formatToUse != 2)) {
            const selection = await this.askForInvoiceFormat();
            formatToUse = selection;
        }
        if (formatToUse == 1) {
            // Formato 1: Ticket (POS)
            useInvoicePrinter(facturaData, this.companyConfig, false);
        } else if (formatToUse == 2) {
            // Formato 2: Legible (Carta / DTE Fiscal)
            useDTEFiscal(facturaData, this.companyConfig, false);
        } else {
            // Se selecciona 0 (Cancelación) o un valor no válido
            self.$swal('Impresión Cancelada', 'No se seleccionó un formato válido.', 'info');
        }
      }
    },
    getStateClass(state) {
        return state === 'EMITTED' ? 'text-warning' : state === 'PAID' ? 'text-success' : 'text-danger';
    },
    getStateText(state) {
        return state === 'EMITTED' ? 'Emitida' : state === 'PAID' ? 'Pagada' : 'Cancelada';
    },
    goBack() {
        this.$router.push("/invoices/credit_notes");
    },
  },
  mounted() {
    const token = localStorage.getItem("accessToken");
    const config = {
      headers: {
        'Content-Type': 'application/json',
        'accept': '*/*',
        'Authorization': `Bearer ${token}`
      }
    };
    this.fetchCustomers(config);
    // 🛑 No se hace fetchProducts
  }
};
</script>

<style scoped>
.card {
  border-radius: 1rem;
}
/* ... (El resto de estilos se mantiene) ... */
</style>